/**
 * User Context Provider for Logging
 * Provides user information across the application for logging purposes
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';
import { logger } from '../utils/Logger';
import { FEATURE_FLAGS } from '../config/featureFlags';

interface UserContextType {
  userEmail?: string;
  displayName?: string;
  sessionId: string;
  isAuthenticated: boolean;
  initializeSession: () => void;
  logUserAction: (action: string, data?: any) => void;
}

const UserContext = createContext<UserContextType>({
  sessionId: '',
  isAuthenticated: false,
  initializeSession: () => {},
  logUserAction: () => {},
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [hasInitialized, setHasInitialized] = useState(false);

  const userEmail = account?.username;
  const displayName = account?.name;
  const isAuthenticated = !!account;

  const initializeSession = useCallback(() => {
    if (userEmail && !hasInitialized) {
      if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING) {
        logger.logAuth({
          userEmail,
          displayName,
          sessionId,
          authAction: 'login_success',
          isAuthorized: true,
          metadata: {
            sessionStart: true,
            userAgent: navigator.userAgent,
          }
        });
      }
      setHasInitialized(true);
    }
  }, [userEmail, displayName, sessionId, hasInitialized]);

  const logUserAction = useCallback((action: string, data?: any) => {
    if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING && userEmail) {
      logger.logUIAction({
        userEmail,
        displayName,
        sessionId,
        uiAction: 'button_click',
        elementId: action,
        metadata: data,
      });
    }
  }, [userEmail, displayName, sessionId]);

  // Initialize session when user becomes available
  useEffect(() => {
    if (isAuthenticated && userEmail) {
      initializeSession();
    }
  }, [isAuthenticated, userEmail, initializeSession]);

  // Log page views
  useEffect(() => {
    if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING && userEmail) {
      logger.logUIAction({
        userEmail,
        displayName,
        sessionId,
        uiAction: 'page_view',
        pageName: window.location.pathname,
        metadata: {
          url: window.location.href,
          referrer: document.referrer,
        }
      });
    }
  }, [userEmail, displayName, sessionId]);

  return (
    <UserContext.Provider value={{
      userEmail,
      displayName,
      sessionId,
      isAuthenticated,
      initializeSession,
      logUserAction,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);

export default UserProvider;
